/**
 * API client for Agent Factory admin endpoints.
 * All calls include Bearer auth from stored key.
 */

const STORAGE_KEY = 'afk_api_key';
const STORAGE_URL = 'afk_api_url';

// ── Auth store ───────────────────────────────────────────────

export function getApiKey(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

export function setApiKey(key: string) {
  localStorage.setItem(STORAGE_KEY, key);
}

export function clearApiKey() {
  localStorage.removeItem(STORAGE_KEY);
}

export function getApiUrl(): string {
  return localStorage.getItem(STORAGE_URL) || import.meta.env.VITE_API_URL || '';
}

export function setApiUrl(url: string) {
  localStorage.setItem(STORAGE_URL, url.replace(/\/+$/, ''));
}

// ── Fetch wrapper ────────────────────────────────────────────

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const key = getApiKey();
  if (!key) throw new ApiError(401, 'No API key configured');

  const base = getApiUrl();
  const url = `${base}${path}`;

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${key}`,
    'Content-Type': 'application/json',
  };

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    clearApiKey();
    throw new ApiError(401, 'Invalid API key');
  }

  const data = await res.json();

  if (!res.ok) {
    throw new ApiError(res.status, data.error || `HTTP ${res.status}`);
  }

  return data as T;
}

// ── Types ────────────────────────────────────────────────────

export interface HealthData {
  status: string;
  agents: number;
  agentNames: string[];
  mesh: { agents: number; reservations: number };
  uptime: number;
  nodeVersion: string;
  pid: number;
  memory: { rss: number; heap: number };
}

export interface AgentData {
  name: string;
  description?: string;
  trigger: string;
  model?: string;
  tools?: string[];
  skills?: string[];
  executionMode: string;
  thinking?: string;
  type?: string;
  capabilities?: string[];
  defaultReads?: string[];
  output?: string;
  systemPrompt?: string;

  // Routing
  dmScope?: string;
  defaultForChannels?: string[];
  bindings?: Record<string, unknown>[];
  isFactoryAgent: boolean;

  // Isolation / memory
  runtimeId?: string;
  sharedScopes?: string[];

  // Mesh
  mesh: boolean;
  meshAutoJoin?: boolean;

  // Pipeline
  pipelinePreset?: string;
  pipelineMaxRetryLoops?: number;

  // Crew
  crewPlannerAgent?: string;
  crewWorkerAgent?: string;
  crewReviewerAgent?: string;
  crewConcurrency?: number;
  crewFailurePolicy?: string;

  // Raw source (only from GET /agents/:name)
  source?: string;
}

export interface ModelInfo {
  provider: string;
  id: string;
  label: string;
}

export interface SessionData {
  id: string;
  agent: string;
  adapter: string;
  channel: string;
  messages: number;
  created: string;
}

export interface MeshData {
  agents: Array<{ id: string; name: string; capabilities?: string[] }>;
  reservations: Array<{ path: string; agentId: string; mode: string }>;
  feed: Array<{ type: string; agentId?: string; agentName?: string; timestamp: number; data?: unknown }>;
}

export interface ConfigData {
  workspace: string;
  defaultModel: string;
  maxRunners: string | number;
}

export interface TestResult {
  handled: boolean;
  agentName?: string;
  response: string;
  sessionKey?: string;
}

// ── API ──────────────────────────────────────────────────────

export const api = {
  health: () => request<HealthData>('GET', '/admin/health'),

  // Agents
  listAgents: () => request<{ agents: AgentData[] }>('GET', '/admin/agents').then(d => d.agents),
  getAgent: (name: string) => request<AgentData>('GET', `/admin/agents/${name}`),
  saveAgent: (name: string, body: Record<string, unknown>) =>
    request<{ message: string; agent: AgentData }>('PUT', `/admin/agents/${name}`, body),
  deleteAgent: (name: string) => request<{ message: string }>('DELETE', `/admin/agents/${name}`),
  testAgent: (name: string, text: string) =>
    request<TestResult>('POST', `/admin/agents/${name}/test`, { text }),

  // Models & tools
  listModels: () =>
    request<{ models: ModelInfo[]; providers: string[] }>('GET', '/admin/models'),
  listTools: () =>
    request<{ tools: string[] }>('GET', '/admin/tools').then(d => d.tools),

  // Sessions
  listSessions: (limit = 50) =>
    request<{ sessions: SessionData[] }>('GET', `/admin/sessions?limit=${limit}`).then(d => d.sessions),

  // Mesh
  mesh: () => request<MeshData>('GET', '/admin/mesh'),

  // Config
  config: () => request<ConfigData>('GET', '/admin/config'),

  // Reload
  reload: () =>
    request<{ message: string; agents: number; agentNames: string[] }>('POST', '/admin/reload'),

  // Auth check
  ping: () => request<HealthData>('GET', '/admin/health'),
};
